import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { useNavigate, Link } from "react-router-dom";

function BrandList() {
  const location = useLocation();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [showInactive, setShowInactive] = useState(false); // ✅ new
  const baseURL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const token = localStorage.getItem("profileToken");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("view") === "inactive") setShowInactive(true);
    if (params.get("view") === "active") setShowInactive(false);
  }, [location.search]);
  useEffect(() => {
    fetchBrands();
    // eslint-disable-next-line
  }, []);

  const fetchBrands = async () => {
    try {
      if (!token) {
        Swal.fire("Unauthorized", "Please login first!", "warning");
        setLoading(false);
        return;
      }
      const res = await axios.get(`${baseURL}/brand/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBrands(res.data.result || []);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setMessage("Error fetching brands");
      setLoading(false);
    }
  };

  // ✅ counts (optional)
  const activeCount = useMemo(
    () => brands.filter((b) => Number(b.status) === 1).length,
    [brands]
  );
  const inactiveCount = useMemo(
    () => brands.filter((b) => Number(b.status) === 0).length,
    [brands]
  );

  // ✅ filtered list to render
  const visibleBrands = useMemo(() => {
    return brands.filter((b) =>
      showInactive ? Number(b.status) === 0 : Number(b.status) === 1
    );
  }, [brands, showInactive]);

  const handleToggleStatus = async (brand) => {
    try {
      await axios.patch(
        `${baseURL}/brand/${brand.id}/status`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const newStatus = Number(brand.status) === 1 ? 0 : 1;

      setBrands((prev) =>
        prev.map((x) => (x.id === brand.id ? { ...x, status: newStatus } : x))
      );

      // ✅ switch view automatically
      setShowInactive(newStatus === 0);

      Swal.fire("Success", "Status updated", "success");
    } catch (err) {
      Swal.fire("Error!", "Failed to update status.", "error");
    }
  };

  const deleteBrand = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This brand will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${baseURL}/brand/delete/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        Swal.fire("Deleted!", "Brand has been deleted.", "success");
        fetchBrands();
      } catch (err) {
        console.error(err);
        Swal.fire("Error!", "Failed to delete brand.", "error");
      }
    }
  };

  if (loading) return <p className="text-center mt-5">Loading brands...</p>;

  return (
    <div className="container my-5 p-4 border rounded shadow bg-white">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0 text-success">
          {showInactive ? "Inactive Brand List" : "Brand List"}{" "}
          <span className="badge bg-secondary ms-2">
            {showInactive ? inactiveCount : activeCount}
          </span>
        </h3>

        <div className="d-flex gap-2">
          {/* ✅ Toggle button */}
          <button
            className={`btn ${showInactive ? "btn-outline-success" : "btn-outline-danger"} rounded-pill px-4`}
            onClick={() => setShowInactive((prev) => !prev)}
            disabled={showInactive ? activeCount === 0 && false : inactiveCount === 0 && false}
            title={showInactive ? "Show Active Brands" : "Show Inactive Brands"}
          >
            {showInactive
              ? `Active Brands (${activeCount})`
              : `Inactive Brands (${inactiveCount})`}
          </button>

          <Link className="btn btn-success rounded-pill px-4" to="/add-brand">
            <i className="fas fa-plus me-2"></i> Add Brand
          </Link>
        </div>
      </div>

      {message && <div className="alert alert-info text-center">{message}</div>}

      <table className="table table-striped table-bordered align-middle text-center">
        <thead className="table-success">
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Logo</th>
            <th>Section BG</th>
            <th>Heading BG</th>
            <th>Gallery BG</th>
            <th>About Brand</th>
            <th>Inner Title</th>
            <th>Inner Content</th>
            <th>Categories</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {visibleBrands.length === 0 ? (
            <tr>
              <td colSpan="12" className="text-center py-4">
                {showInactive ? "No inactive brands found." : "No active brands found."}
              </td>
            </tr>
          ) : (
            visibleBrands.map((brand, index) => (
              <tr
                key={brand.id}
                // ✅ inactive row highlight
                className={Number(brand.status) === 0 ? "table-secondary" : ""}
                style={Number(brand.status) === 0 ? { opacity: 0.85 } : {}}
              >
                <td>{index + 1}</td>

                <td>
                  {brand.name || "-"}{" "}
                  {Number(brand.status) === 0 && (
                    <span className="badge bg-danger ms-2">Inactive</span>
                  )}
                </td>

                <td>
                  {brand.logo ? (
                    <img
                      src={`${baseURL}/uploads/${brand.logo}`}
                      alt="Logo"
                      style={{
                        width: "60px",
                        height: "60px",
                        objectFit: "contain",
                        borderRadius: "8px",
                      }}
                    />
                  ) : (
                    "-"
                  )}
                </td>

                <td>
                  {brand.section_bg ? (
                    <img
                      src={`${baseURL}/uploads/${brand.section_bg}`}
                      alt="Section BG"
                      style={{
                        width: "80px",
                        height: "60px",
                        objectFit: "cover",
                        borderRadius: "8px",
                      }}
                    />
                  ) : (
                    "-"
                  )}
                </td>

                <td>
                  {brand.heading_bg ? (
                    <img
                      src={`${baseURL}/uploads/${brand.heading_bg}`}
                      alt="Heading BG"
                      style={{
                        width: "80px",
                        height: "60px",
                        objectFit: "cover",
                        borderRadius: "8px",
                      }}
                    />
                  ) : (
                    "-"
                  )}
                </td>

                <td>
                  {brand.gallery_bg ? (
                    <img
                      src={`${baseURL}/uploads/${brand.gallery_bg}`}
                      alt="Gallery BG"
                      style={{
                        width: "80px",
                        height: "60px",
                        objectFit: "cover",
                        borderRadius: "8px",
                      }}
                    />
                  ) : (
                    "-"
                  )}
                </td>

                <td>{brand.about_brand ? brand.about_brand.slice(0, 60) + "..." : "-"}</td>
                <td>{brand.inner_title || "-"}</td>
                <td>{brand.inner_content ? brand.inner_content.slice(0, 60) + "..." : "-"}</td>

                <td>
                  <button
                    onClick={() => navigate(`/update-brand/${brand.id}#categories`)}
                    className="btn btn-sm btn-info"
                  >
                    View
                  </button>
                </td>

                <td>
                  <div className="form-check form-switch d-flex justify-content-center">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={Number(brand.status) === 1}
                      onChange={() => handleToggleStatus(brand)}
                    />
                  </div>
                </td>

                <td>
                  <button
                    className="btn btn-sm btn-primary me-2"
                    title="Edit"
                    onClick={() => navigate(`/update-brand/${brand.id}`)}
                  >
                    <i className="fa fa-edit"></i>
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    title="Delete"
                    onClick={() => deleteBrand(brand.id)}
                  >
                    <i className="fa fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default BrandList;
